import { useEffect, useMemo } from 'react';
import '@/i18n/i18n';
import { MantineProvider } from '@mantine/core';
import { AuthProvider as OidcAuthProvider } from 'react-oidc-context';
import { AxiosError } from 'axios';
import '@mantine/core/styles.css';
import '@mantine/dates/styles.css';
import '@mantine/notifications/styles.css';
import './styles/index.sass';
import { Notifications } from '@mantine/notifications';
import { QueryCache, QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ModalsProvider } from '@mantine/modals';
import { AuthProvider, GlobalSettingsProvider, PartitionProvider, ScreenSizeProvider, usePartition } from '@/providers';
import { AppVersionNotifier } from '@/components/AppVersionNotifier/AppVersionNotifier';
import { Routes } from '@/routing';
import { cssVariablesResolver, getThemeForPartition } from '@/styles/theme';
import { handleError, isRequestCancelledError, setCSSVariables } from '@/helpers';
import { isInsufficientPermissionsRedirectError, isRedirectToSuperadminError } from '@/helpers/forbiddenRedirect';
import { NOTIFICATION_AUTO_CLOSE_TIME } from './constants';
import { cognitoUserManager } from '@/configs/cognitoAuthConfig';
import AuthService from '@/services/auth.service';

const queryClient = new QueryClient({
  queryCache: new QueryCache({
    onError: (error, query) => {
      if (isRequestCancelledError(error)) {
        return;
      }

      if (query.meta?.suppressGlobalError) {
        return;
      }

      handleError(error);
    },
  }),
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      retry: (failureCount, error) => {
        if (
          isRequestCancelledError(error) ||
          isInsufficientPermissionsRedirectError(error) ||
          isRedirectToSuperadminError(error)
        ) {
          return false;
        }

        // Client/validation errors will not succeed on retry; keep retrying them causes infinite loaders.
        if (error instanceof AxiosError && error.response && error.response.status < 500) {
          return false;
        }

        return failureCount < 2;
      },
    },
  },
});

const App = () => {
  useEffect(() => {
    setCSSVariables();
  }, []);

  const handleSigninCallback = (): void => {
    window.history.replaceState({}, document.title, window.location.pathname);
  };

  return (
    <OidcAuthProvider
      userManager={cognitoUserManager}
      skipSigninCallback={AuthService.hasLogoutIntent()}
      onSigninCallback={handleSigninCallback}
    >
      <PartitionProvider>
        <AppMantineProvider />
      </PartitionProvider>
    </OidcAuthProvider>
  );
};

const AppMantineProvider = () => {
  const { partition } = usePartition();
  const dynamicTheme = useMemo(() => getThemeForPartition({ partition }), [partition]);

  return (
    <MantineProvider theme={dynamicTheme} cssVariablesResolver={cssVariablesResolver}>
      <ModalsProvider>
        <GlobalSettingsProvider>
          <Notifications autoClose={NOTIFICATION_AUTO_CLOSE_TIME} />
          <AppVersionNotifier />
          <QueryClientProvider client={queryClient}>
            <AuthProvider>
              <ScreenSizeProvider>
                <Routes />
              </ScreenSizeProvider>
            </AuthProvider>
          </QueryClientProvider>
        </GlobalSettingsProvider>
      </ModalsProvider>
    </MantineProvider>
  );
};

export default App;
